import { Capacitor } from '@capacitor/core';
import { CapacitorSQLite, SQLiteConnection } from '@capacitor-community/sqlite';
import {CREATE_TABLE, CREATE_TABLE_SCORE, ALTER_TABLE_1, ALTER_TABLE_2, ALTER_TABLE_3, ALTER_TABLE_4, ALTER_TABLE_5, ALTER_TABLE_6, ALTER_TABLE_7, ALTER_TABLE_8, ALTER_TABLE_9} from '@/utils/db-create';
import {DROP_TABLE} from '@/utils/db-drop';

// Bump this when adding a new ALTER_TABLE_x migration below.
const SCHEMA_VERSION = 9;
// Last version covered by the try/catch legacy alters (1-7).
const LEGACY_SCHEMA_VERSION = 7;

// Score table columns added from v8 on, in order. A version is only reached
// once every column up to and including it exists on the device.
const SCORE_COLUMN_MIGRATIONS = [
    { version: 8, alters: ALTER_TABLE_8, columns: ['tech2fouls', 'disrfouls'] },
    { version: 9, alters: ALTER_TABLE_9, columns: ['benchtechfouls'] },
];

const sqliteService = () => {
    const platform = Capacitor.getPlatform();
    let db;

    const databaseStartup = async () => {
        const sqlite = new SQLiteConnection(CapacitorSQLite);

        try {
            // console.log(platform);
            if(platform === "web") {
                // Create the 'jeep-sqlite' Stencil component
                const jeepSqlite = document.createElement('jeep-sqlite');
                // Versioned path (see the prebuild script): the engine binary
                // must match the installed jeep-sqlite/sql.js, and a fresh URL
                // keeps stale HTTP caches from serving an old engine.
                jeepSqlite.wasmPath = '/assets/sqljs';
                document.body.appendChild(jeepSqlite);
                await customElements.whenDefined('jeep-sqlite');
                // Initialize the Web store
                await sqlite.initWebStore();
                // console.log('init web store');
            }

            const ret = await sqlite.checkConnectionsConsistency();
            const isConn = (await sqlite.isConnection('lmp-db', false)).result;

            if (ret.result && isConn) {
                db = await sqlite.retrieveConnection('lmp-db', false);
                // console.log('retrieve connection');
            }
            else {
                db = await sqlite.createConnection('lmp-db', false, 'no-encryption', 1, false);
                // console.log('create connection');
            }

            if (!db) throw new Error("NO DATABASE");

            await db.open();

            // console.log("database opened", db);

            return db;
        } catch (error) {
            console.error('error init db', error);
            // Surface the failure — a silently-undefined db makes every later
            // query fail with cryptic errors far from the real cause.
            throw new Error('Database failed to start: ' + (error && error.message ? error.message : error));
        }
    };

    const closeDb = async (db) => {
        try {
            await db.close();
        } catch (error) {
            // console.log('error close db', error);
            throw new Error(error);
        }
    };

    const openDb = async (db) => {
        try {
            await db.open();
        } catch (error) {
            // console.log('error open db', error);
            throw new Error(error);
        }
    };

    const getSchemaVersion = async () => {
        try {
            const resp = await db.query('PRAGMA user_version;');
            if (resp.values && resp.values.length > 0) {
                return resp.values[0].user_version || 0;
            }
        } catch (err) {
            // Older plugin versions may not return PRAGMA results — treat as legacy.
        }
        return 0;
    };

    // A zero-row SELECT fails with "no such column" if any is missing,
    // without relying on PRAGMA output (older plugin versions may not return it).
    const scoreColumnsExist = async (columns) => {
        try {
            await db.query(`SELECT ${columns.join(', ')} FROM lmp_ng_scores_app LIMIT 0;`);
            return true;
        } catch (err) {
            return false;
        }
    };

    const initDbTables = async () => {
        try {
            // let resp_drop = await db.execute(DROP_TABLE); //@TODO remove for PROD
            let resp = await db.execute(CREATE_TABLE);
            let resp_score = await db.execute(CREATE_TABLE_SCORE);

            const currentVersion = await getSchemaVersion();

            // Devices in the field predate user_version stamping, so migrations
            // 1-7 must stay individually idempotent (try/catch). Once every
            // device reports SCHEMA_VERSION, new migrations can rely on the
            // version check alone.
            const legacyAlters = [ALTER_TABLE_1, ALTER_TABLE_2, ALTER_TABLE_3, ALTER_TABLE_4, ALTER_TABLE_5, ALTER_TABLE_6, ALTER_TABLE_7];

            if (currentVersion < LEGACY_SCHEMA_VERSION) {
                for (const alter of legacyAlters) {
                    try {
                        await db.execute(alter);
                    } catch (err) {
                        // Column/table already present on this device.
                    }
                }
            }

            for (const migration of SCORE_COLUMN_MIGRATIONS) {
                if (currentVersion < migration.version) {
                    for (const alter of migration.alters) {
                        try {
                            await db.execute(alter);
                        } catch (err) {
                            // Column already present (fresh install: CREATE_TABLE_SCORE has it).
                        }
                    }
                }
            }

            if (currentVersion < SCHEMA_VERSION) {
                // Stamp a version only once its columns really exist. Stamping
                // after a failed ALTER would stop it ever being retried, and
                // every score save on this device would then fail.
                let reachedVersion = Math.max(currentVersion, LEGACY_SCHEMA_VERSION);
                let missingColumns = [];

                for (const migration of SCORE_COLUMN_MIGRATIONS) {
                    if (!(await scoreColumnsExist(migration.columns))) {
                        missingColumns = migration.columns;
                        break;
                    }
                    reachedVersion = migration.version;
                }

                if (reachedVersion > currentVersion) {
                    await db.execute(`PRAGMA user_version = ${reachedVersion};`, false);
                }

                if (missingColumns.length) {
                    // Shows the storage error banner; reopening the app retries.
                    throw new Error('Score table is missing the ' + missingColumns.join('/') + ' columns');
                }
            }

            if(resp.changes && resp.changes.changes && resp.changes.changes < 0) {
                throw new Error('Error: created failed');
            }

            // console.log('init tables');
        } catch (error) {
            // console.log('error init tables', error);
            throw new Error(error);
        }
    };

    return {
        // FUNCTIONS
        databaseStartup,
        initDbTables,
        openDb,
        closeDb,
    };
};

export default sqliteService;